import { useMemo } from 'react';
import { darken, lighten, useTheme } from '@mui/material/styles';
import useAuth from '../../hooks/useAuth';
import { CoBranding } from '../../@types/common.types';

/** The colors a co-branded surface uses, all running from the carrier's primary to our brand blue. */
export interface CoBrandingColors {
    /** Text drawn in the brand colors, darkened to stay readable */
    textGradient: string;
    /** Banner backgrounds: pale tints of both colors */
    backgroundGradient: string;
    start: string;
    end: string;
}

const linear = (angle: string, from: string, to: string) =>
    `linear-gradient(${angle}, ${from} 0%, ${to} 100%)`;

/**
 * The signed-in client's carrier co-branding, with the colors derived from it. Gradients
 * start on the carrier's primary (falling back to its secondary, then the theme's primary)
 * and end on our brand blue.
 */
const useCoBranding = (): { coBranding: CoBranding | null; colors: CoBrandingColors | null } => {
    const { user, isClient } = useAuth();
    const theme = useTheme();

    const coBranding = isClient ? user?.coBranding ?? null : null;

    const colors = useMemo<CoBrandingColors | null>(() => {
        if (!coBranding) return null;
        const start =
            coBranding.brandColors?.primary ??
            coBranding.brandColors?.secondary ??
            theme.palette.primary.main;
        const end = theme.palette.brand.blue;

        return {
            start,
            end,
            textGradient: linear('90deg', darken(start, 0.2), darken(end, 0.2)),
            backgroundGradient: linear('90deg', lighten(start, 0.86), lighten(end, 0.86)),
        };
    }, [coBranding, theme.palette.primary.main, theme.palette.brand.blue]);

    return { coBranding, colors };
};

export default useCoBranding;
